import React, { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Progress,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
} from '@chakra-ui/react';
import { DataSkeleton, EmptyState, ErrorState } from '../states';
import { useAnalyticsData } from '../../hooks/useAnalyticsData';
import { useAuth } from '../../providers/auth';

interface AnalyticsModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleId?: number;
  weekId?: number;
  materialId?: number;
  type: 'module' | 'week' | 'material';
}

export const AnalyticsModal: React.FC<AnalyticsModalProps> = ({
  isOpen,
  onClose,
  moduleId,
  weekId,
  materialId,
  type,
}) => {
  const { user } = useAuth();
  const { data, loading, hasAccess, error, fetchData } = useAnalyticsData(user);

  // Determinar endpoint baseado no tipo
  const getEndpoint = () => {
    if (type === 'module' && moduleId) {
      return `/modules/${moduleId}/weeks`;
    }
    if (type === 'week' && weekId) {
      return `/weeks/${weekId}/meetings`;
    }
    if (type === 'material' && materialId) {
      return `/materials/${materialId}`;
    }
    return '/modules';
  };

  // Buscar dados quando o modal abrir (lazy loading)
  useEffect(() => {
    if (isOpen && hasAccess) {
      const endpoint = getEndpoint();
      fetchData(endpoint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasAccess]);

  const renderModuleStats = () => {
    if (!data || !data.weeks) return null;

    return (
      <Box>
        <Flex gap={4} mb={6} flexWrap="wrap">
          <Stat flex="1" minW="200px" bg="blue.50" p={4} borderRadius="md">
            <StatLabel>Total de Semanas</StatLabel>
            <StatNumber>{data.summary?.total_weeks || 0}</StatNumber>
          </Stat>
          <Stat flex="1" minW="200px" bg="green.50" p={4} borderRadius="md">
            <StatLabel>Total de Usuários</StatLabel>
            <StatNumber>{data.summary?.total_users || 0}</StatNumber>
          </Stat>
        </Flex>

        <Text fontWeight="bold" mb={3}>
          📊 Estatísticas por Semana:
        </Text>

        <Flex direction="column" gap={4}>
          {data.weeks.map((week: any) => (
            <Box key={week.week_id} p={4} bg="gray.50" borderRadius="md">
              <Text fontWeight="semibold" mb={2}>
                {week.week_name} (Semana {week.week_number})
              </Text>

              <Tabs size="sm" variant="enclosed">
                <TabList>
                  <Tab>Professores</Tab>
                  <Tab>Tutores</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>
                    <Flex justify="space-between" mb={2}>
                      <Text fontSize="sm">
                        {week.professors.accessed}/{week.professors.total} acessaram
                      </Text>
                      <Badge colorScheme={week.professors.percentage > 70 ? 'green' : 'yellow'}>
                        {week.professors.percentage.toFixed(1)}%
                      </Badge>
                    </Flex>
                    <Progress
                      value={week.professors.percentage}
                      colorScheme={week.professors.percentage > 70 ? 'green' : 'yellow'}
                      borderRadius="full"
                    />
                  </TabPanel>

                  <TabPanel>
                    <Flex justify="space-between" mb={2}>
                      <Text fontSize="sm">
                        {week.tutors.accessed}/{week.tutors.total} acessaram
                      </Text>
                      <Badge colorScheme={week.tutors.percentage > 70 ? 'green' : 'yellow'}>
                        {week.tutors.percentage.toFixed(1)}%
                      </Badge>
                    </Flex>
                    <Progress
                      value={week.tutors.percentage}
                      colorScheme={week.tutors.percentage > 70 ? 'green' : 'yellow'}
                      borderRadius="full"
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          ))}
        </Flex>
      </Box>
    );
  };

  const renderWeekStats = () => {
    if (!data) return null;

    return (
      <Box>
        <Flex gap={4} mb={6} flexWrap="wrap">
          <Stat flex="1" minW="200px" bg="blue.50" p={4} borderRadius="md">
            <StatLabel>Total de Usuários</StatLabel>
            <StatNumber>{data.total_users || 0}</StatNumber>
          </Stat>
          <Stat flex="1" minW="200px" bg="green.50" p={4} borderRadius="md">
            <StatLabel>Acessaram</StatLabel>
            <StatNumber>{data.accessed?.count || 0}</StatNumber>
            <StatHelpText>
              <Badge colorScheme="green">{data.accessed?.percentage.toFixed(1)}%</Badge>
            </StatHelpText>
          </Stat>
          <Stat flex="1" minW="200px" bg="red.50" p={4} borderRadius="md">
            <StatLabel>Não Acessaram</StatLabel>
            <StatNumber>{data.not_accessed?.count || 0}</StatNumber>
          </Stat>
        </Flex>

        <Tabs variant="enclosed">
          <TabList>
            <Tab>Acessaram ({data.accessed?.count || 0})</Tab>
            <Tab>Não Acessaram ({data.not_accessed?.count || 0})</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <Box maxH="400px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Email</Th>
                      <Th>Grupo</Th>
                      <Th>Último Acesso</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.accessed?.users?.map((userItem: any) => (
                      <Tr key={userItem.id}>
                        <Td>
                          <Flex align="center" gap={2}>
                            <Avatar size="xs" name={userItem.name} />
                            <Text fontSize="sm">{userItem.name}</Text>
                          </Flex>
                        </Td>
                        <Td fontSize="sm">{userItem.email}</Td>
                        <Td>
                          <Badge size="sm" colorScheme="blue">
                            {userItem.group_name}
                          </Badge>
                        </Td>
                        <Td fontSize="xs" color="gray.600">
                          {userItem.last_access
                            ? new Date(userItem.last_access).toLocaleString('pt-BR')
                            : '-'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>

            <TabPanel>
              <Box maxH="400px" overflowY="auto">
                <Table size="sm" variant="simple">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Email</Th>
                      <Th>Grupo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.not_accessed?.users?.map((userItem: any) => (
                      <Tr key={userItem.id}>
                        <Td>
                          <Flex align="center" gap={2}>
                            <Avatar size="xs" name={userItem.name} />
                            <Text fontSize="sm">{userItem.name}</Text>
                          </Flex>
                        </Td>
                        <Td fontSize="sm">{userItem.email}</Td>
                        <Td>
                          <Badge size="sm" colorScheme="gray">
                            {userItem.group_name}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    );
  };

  const getTitle = () => {
    if (type === 'module') return `📊 Estatísticas do Módulo`;
    if (type === 'week') return `📊 Estatísticas da Semana`;
    if (type === 'material') return `📊 Estatísticas do Material`;
    return '📊 Estatísticas';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="blue.500" color="white" borderTopRadius="md">
          {getTitle()}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          {!hasAccess && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Você não tem permissão para visualizar estatísticas.
            </Alert>
          )}

          {hasAccess && loading && (
            <DataSkeleton variant="cards" rows={3} label="Carregando estatísticas" />
          )}

          {hasAccess && !loading && error && (
            <ErrorState onRetry={() => fetchData(getEndpoint())} />
          )}

          {hasAccess && !loading && !error && data && (
            <>
              {type === 'module' && renderModuleStats()}
              {type === 'week' && renderWeekStats()}
            </>
          )}

          {hasAccess && !loading && !error && !data && (
            <EmptyState
              title="Nenhuma estatística encontrada"
              description="Não existem dados disponíveis para este item."
            />
          )}

          <Text fontSize="xs" color="gray.500" mt={6} textAlign="right">
            Última atualização:{' '}
            {data?.last_updated ? new Date(data.last_updated).toLocaleString('pt-BR') : '-'}
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
